import { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash-es/debounce';
import { LockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { selectCampaignId } from '@/redux/session/selectors';
import type { ContextVariableOption } from '@/shared/types/autocomplete';

import { getCampaignGuidelinesEditorDictionary } from './campaignGuidelinesEditorConfig';
import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import { selectDraftSubscribers } from '../../redux/selectors';
import { getCampaignContextVariableOptions, getDraftContextVariableOptions } from '../../utils';

interface CampaignGuidelinesProps {
  initialMarkdown?: string;
  onChange?: (markdown: string) => void;
  editable?: boolean;
}

/**
 * Campaign Guidelines component with a rich text editor.
 * Fetches subscriber variables internally — no variable props needed from parents.
 */
export const CampaignGuidelines = ({
  initialMarkdown = '',
  onChange,
  editable = true,
}: CampaignGuidelinesProps) => {
  const { t } = useTranslation();
  const dictionary = getCampaignGuidelinesEditorDictionary();

  // --- Subscriber variable resolution (internal, not prop-based) ---
  const campaignId = useSelector(selectCampaignId);
  const draftSubscribers = useSelector(selectDraftSubscribers);
  const { campaign_subscribers: apiSubscribers } = useCampaignSubscribers(campaignId);

  const variableOptions = useMemo<ContextVariableOption[]>(
    () =>
      (draftSubscribers.length > 0
        ? getDraftContextVariableOptions(draftSubscribers)
        : getCampaignContextVariableOptions(apiSubscribers || [])
      ).map((opt) => ({
        label: opt.label || opt.value || '',
        value: opt.value || '',
        type: 'context_variable' as const,
      })),
    [draftSubscribers, apiSubscribers]
  );

  // --- Debounced onChange via onEditorReady + onChange ---
  const getMarkdownRef = useRef<() => Promise<string>>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedChange = useMemo(
    () =>
      debounce(async () => {
        if (!getMarkdownRef.current || !onChangeRef.current) {
          return;
        }
        const markdown = await getMarkdownRef.current();
        onChangeRef.current(markdown);
      }, 300),
    []
  );

  useEffect(() => () => debouncedChange.cancel(), [debouncedChange]);

  const handleEditorReady = useCallback(
    (editorApi: {
      appendContent: (markdown: string) => void;
      getMarkdown: () => Promise<string>;
    }) => {
      getMarkdownRef.current = editorApi.getMarkdown;
    },
    []
  );

  const handleChange = useCallback(() => {
    debouncedChange();
  }, [debouncedChange]);

  return (
    <div className="flex w-[500px] flex-col p-6">
      <h2 className="text-lg font-semibold text-foreground">
        {t('campaigns.builddebtcampaign.guidelines.title')}
      </h2>
      <div className="mt-4 flex-1">
        <h3 className="mb-4 flex items-center gap-1.5 text-lg leading-7 font-semibold text-muted-foreground">
          <LockIcon className="size-3" />
          {t('campaigns.builddebtcampaign.guidelines.channel_usage')}
        </h3>
        <BlockNoteEditorComponent
          initialMarkdown={initialMarkdown}
          dictionary={dictionary}
          contextVariableOptions={variableOptions}
          onEditorReady={handleEditorReady}
          onChange={handleChange}
          editable={editable}
        />
      </div>
    </div>
  );
};
